import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import {
  Card,
  Button,
  Input,
  Textarea,
  PageHeader,
  Spinner,
  EmptyState,
  Modal,
  Field,
  Tabs,
  Badge,
  ErrorBanner,
  formatDate,
  type BadgeColor,
} from '../components/ui';

interface SocialAccount {
  id: string;
  platform: 'facebook' | 'instagram' | 'twitter' | 'linkedin' | 'tiktok';
  handle: string;
  status: 'connected' | 'disconnected';
}

interface MediaItem {
  id: string;
  filename: string;
  url: string;
  type: 'image' | 'video';
}

interface SocialPost {
  id: string;
  body: string;
  platforms: string[];
  status: 'draft' | 'scheduled' | 'published' | 'failed';
  scheduledAt?: string;
  mediaIds?: string[];
}

const statusColor: Record<SocialPost['status'], BadgeColor> = {
  draft: 'gray',
  scheduled: 'blue',
  published: 'green',
  failed: 'red',
};

type Tab = 'accounts' | 'media' | 'composer' | 'calendar' | 'analytics';

export function SocialPage() {
  const [tab, setTab] = useState<Tab>('composer');
  return (
    <div>
      <PageHeader title="Social" subtitle="Schedule, publish, analyze across platforms" />
      <Tabs<Tab>
        active={tab}
        onChange={setTab}
        tabs={[
          { value: 'composer', label: 'Composer' },
          { value: 'calendar', label: 'Calendar' },
          { value: 'media', label: 'Media' },
          { value: 'accounts', label: 'Accounts' },
          { value: 'analytics', label: 'Analytics' },
        ]}
      />
      {tab === 'composer' && <Composer />}
      {tab === 'calendar' && <CalendarView />}
      {tab === 'media' && <Media />}
      {tab === 'accounts' && <Accounts />}
      {tab === 'analytics' && <Analytics />}
    </div>
  );
}

function Accounts() {
  const q = useQuery({
    queryKey: ['social-accounts'],
    queryFn: () => api.get<{ data: SocialAccount[] }>('/social/accounts').catch(() => ({ data: [] })),
  });
  const accounts = q.data?.data ?? [];
  const platforms: SocialAccount['platform'][] = ['facebook', 'instagram', 'twitter', 'linkedin', 'tiktok'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <Card>
        <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-base)', fontWeight: 600 }}>Connect a platform</h3>
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          {platforms.map((p) => {
            const connected = accounts.find((a) => a.platform === p);
            return (
              <Button
                key={p}
                variant={connected ? 'secondary' : 'primary'}
                onClick={() => {
                  const url = `${(import.meta.env.VITE_API_URL as string | undefined) ?? ''}/api/v1/social/oauth/${p}/start`;
                  window.location.href = url;
                }}
              >
                {connected ? `✓ ${p}` : `+ Connect ${p}`}
              </Button>
            );
          })}
        </div>
      </Card>
      {q.isLoading ? (
        <Spinner />
      ) : accounts.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {accounts.map((a) => (
            <Card key={a.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>@{a.handle}</div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>{a.platform}</div>
                </div>
                <Badge color={a.status === 'connected' ? 'green' : 'red'}>{a.status}</Badge>
              </div>
            </Card>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function Media() {
  const q = useQuery({
    queryKey: ['social-media'],
    queryFn: () => api.get<{ data: MediaItem[] }>('/social/media').catch(() => ({ data: [] })),
  });
  const media = q.data?.data ?? [];
  if (q.isLoading) return <Spinner />;
  if (media.length === 0) return <Card><EmptyState title="No media yet" description="Upload images and videos to use in posts." /></Card>;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 'var(--space-2)' }}>
      {media.map((m) => (
        <Card key={m.id} style={{ padding: 'var(--space-2)' }}>
          {m.type === 'image' ? (
            <img src={m.url} alt={m.filename} style={{ width: '100%', borderRadius: 'var(--radius-md)' }} />
          ) : (
            <video src={m.url} controls style={{ width: '100%', borderRadius: 'var(--radius-md)' }} />
          )}
          <div style={{ fontSize: 'var(--text-xs)', marginTop: 4 }}>{m.filename}</div>
        </Card>
      ))}
    </div>
  );
}

function Composer() {
  const qc = useQueryClient();
  const [body, setBody] = useState('');
  const [platforms, setPlatforms] = useState<string[]>(['facebook', 'instagram']);
  const [scheduledAt, setScheduledAt] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      api.post('/social/posts', {
        body,
        platforms,
        scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
        status: scheduledAt ? 'scheduled' : 'draft',
      }),
    onSuccess: () => {
      setBody('');
      setScheduledAt('');
      qc.invalidateQueries({ queryKey: ['social-posts'] });
    },
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Failed'),
  });

  return (
    <Card>
      <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-base)', fontWeight: 600 }}>New post</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <Field label="Post text">
          <Textarea value={body} onChange={setBody} rows={4} placeholder="What's on your mind?" />
        </Field>
        <div>
          <div style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: 'var(--space-2)' }}>
            Platforms
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {['facebook', 'instagram', 'twitter', 'linkedin', 'tiktok'].map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPlatforms(platforms.includes(p) ? platforms.filter((x) => x !== p) : [...platforms, p])}
                style={{
                  padding: 'var(--space-1) var(--space-3)',
                  borderRadius: 'var(--radius-full)',
                  border: '1px solid var(--color-border-primary)',
                  background: platforms.includes(p) ? 'var(--color-brand-primary)' : 'var(--color-surface-primary)',
                  color: platforms.includes(p) ? 'white' : 'var(--color-text-primary)',
                  cursor: 'pointer',
                  fontSize: 'var(--text-sm)',
                }}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
        <Field label="Schedule for (optional)">
          <Input value={scheduledAt} onChange={setScheduledAt} type="datetime-local" />
        </Field>
        <div>
          <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: 'var(--space-2)' }}>Preview</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'var(--space-2)' }}>
            {platforms.map((p) => (
              <Card key={p} style={{ padding: 'var(--space-3)', background: 'var(--color-surface-secondary)' }}>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginBottom: 4 }}>{p}</div>
                <div style={{ fontSize: 'var(--text-sm)', whiteSpace: 'pre-wrap' }}>{body || 'Your post will appear here…'}</div>
              </Card>
            ))}
          </div>
        </div>
        <ErrorBanner error={error} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <Button onClick={() => body && platforms.length > 0 && mut.mutate()} disabled={!body || platforms.length === 0 || mut.isPending}>
            {scheduledAt ? 'Schedule post' : 'Save draft'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function CalendarView() {
  const q = useQuery({
    queryKey: ['social-posts'],
    queryFn: () => api.get<{ data: SocialPost[] }>('/social/posts').catch(() => ({ data: [] })),
  });
  if (q.isLoading) return <Spinner />;
  const posts = q.data?.data ?? [];
  if (posts.length === 0) return <Card><EmptyState title="No scheduled posts" /></Card>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      {posts.map((p) => (
        <Card key={p.id}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 'var(--text-sm)', whiteSpace: 'pre-wrap' }}>{p.body}</div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                {p.platforms.join(', ')} · {p.scheduledAt ? `Scheduled ${formatDate(p.scheduledAt)}` : 'Not scheduled'}
              </div>
            </div>
            <Badge color={statusColor[p.status]}>{p.status}</Badge>
          </div>
        </Card>
      ))}
    </div>
  );
}

function Analytics() {
  const q = useQuery({
    queryKey: ['social-analytics'],
    queryFn: () => api.get<{ followers: number; postsLast30: number; impressions: number; engagement: number }>('/social/analytics').catch(() => ({ followers: 0, postsLast30: 0, impressions: 0, engagement: 0 })),
  });
  if (q.isLoading) return <Spinner />;
  const data = q.data!;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-3)' }}>
      <StatCard label="Followers" value={data.followers} />
      <StatCard label="Posts (30d)" value={data.postsLast30} />
      <StatCard label="Impressions" value={data.impressions} />
      <StatCard label="Engagement" value={`${data.engagement}%`} />
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number | string }) {
  return (
    <Card>
      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{label}</div>
      <div style={{ fontSize: 'var(--text-3xl)', fontWeight: 700 }}>{value}</div>
    </Card>
  );
}
